import { Trophy, ChevronUp, ChevronDown, Minus, Download } from "lucide-react";
import { useState } from "react";
import { API } from "../../lib/api";
import { useI18n } from "../../i18n";
import HallOfFameModal from "./HallOfFameModal";

function InsightsBanner({ insights, t }) {
    const { mvp, biggest_climber, biggest_dropper, latest_round } = insights;
    const cards = [];
    if (mvp) cards.push({
        key: "mvp",
        accent: "text-[var(--accent-hex)] border-[var(--accent-border)]",
        label: latest_round ? t("leader.mvp_round", { round: latest_round }) : t("leader.mvp"),
        body: <><span className="font-bold">{mvp.user_name}</span> · {mvp.points}p</>,
    });
    if (biggest_climber) cards.push({
        key: "climber",
        accent: "text-[#00FF66] border-[#00FF66]/40",
        label: t("leader.climber"),
        body: <><span className="font-bold">{biggest_climber.user_name}</span> ↑{biggest_climber.gained}</>,
    });
    if (biggest_dropper) cards.push({
        key: "dropper",
        accent: "text-[#FF6B6B] border-[#FF6B6B]/40",
        label: t("leader.dropper"),
        body: <><span className="font-bold">{biggest_dropper.user_name}</span> ↓{biggest_dropper.lost}</>,
    });
    if (cards.length === 0) return null;
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-4" data-testid="leaderboard-insights">
            {cards.map((c) => (
                <div key={c.key} className={`rounded-md border bg-[var(--bg-card)] p-3 ${c.accent}`} data-testid={`insight-${c.key}`}>
                    <div className="text-[9px] uppercase tracking-widest font-bold opacity-80">{c.label}</div>
                    <div className="mt-1 text-sm text-[var(--text-primary)]">{c.body}</div>
                </div>
            ))}
        </div>
    );
}

export default function Leaderboard({ rows, insights, currentUserId, onRefresh, canRecalc, onRecalc, poolId, pool }) {
    const { t } = useI18n();
    const [hofOpen, setHofOpen] = useState(false);
    if (!rows.length) {
        return (
            <div className="text-center py-16" data-testid="leaderboard-empty">
                <Trophy className="h-10 w-10 text-[var(--accent-hex)] mx-auto mb-3" />
                <p className="text-[var(--text-secondary)]">{t("leader.empty.title")}</p>
                {canRecalc && (
                    <button onClick={onRecalc} className="mt-4 inline-flex items-center gap-2 bg-[var(--accent-hex)] text-[var(--accent-text)] font-extrabold uppercase tracking-widest text-xs px-4 py-2 rounded-md" data-testid="recalc-empty">
                        {t("leader.empty.force")}
                    </button>
                )}
            </div>
        );
    }
    const csvUrl = `${API}/pools/${poolId}/predictions.csv`;
    return (
        <div data-testid="leaderboard-view">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-2xl font-bold uppercase text-[var(--text-primary)]">{t("leader.title")}</h3>
                <div className="flex gap-2 flex-wrap">
                    <button onClick={onRefresh} data-testid="leaderboard-refresh" className="text-xs uppercase tracking-widest font-bold text-[var(--text-secondary)] hover:text-[var(--text-primary)] px-3 py-1.5 border border-[var(--border-base)] rounded-md">{t("leader.refresh")}</button>
                    {canRecalc && (
                        <>
                            <a href={csvUrl} download data-testid="leaderboard-csv" className="text-xs uppercase tracking-widest font-bold text-[var(--text-secondary)] hover:text-[var(--text-primary)] px-3 py-1.5 border border-[var(--border-base)] rounded-md">
                                {t("leader.csv")}
                            </a>
                            <button onClick={onRecalc} data-testid="leaderboard-recalc" className="text-xs uppercase tracking-widest font-bold text-[var(--accent-text)] bg-[var(--accent-hex)] hover:bg-[var(--accent-hex-hover)] px-3 py-1.5 rounded-md">{t("leader.recalc")}</button>
                        </>
                    )}
                </div>
            </div>

            {/* Prominent full-width CTA — unmissable, distinct from the "Hall of fame" MVP tab. */}
            <button
                onClick={() => setHofOpen(true)}
                data-testid="leaderboard-hall-of-fame"
                className="group w-full flex items-center justify-between gap-3 mb-4 px-5 py-4 rounded-lg border-2 border-[var(--accent-hex)] bg-gradient-to-r from-[var(--accent-hex)]/15 to-[var(--accent-hex)]/5 hover:from-[var(--accent-hex)]/25 hover:to-[var(--accent-hex)]/10 transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-md bg-[var(--accent-hex)] text-[var(--accent-text)] flex items-center justify-center shrink-0">
                        <Download className="h-5 w-5" />
                    </div>
                    <div className="text-left min-w-0">
                        <div className="font-heading text-base sm:text-lg font-extrabold uppercase tracking-tight text-[var(--text-primary)] truncate">{t("leader.summary.title")}</div>
                        <div className="text-xs text-[var(--text-secondary)] truncate">{t("leader.summary.subtitle")}</div>
                    </div>
                </div>
                <span className="text-[10px] uppercase tracking-widest font-extrabold text-[var(--accent-text)] bg-[var(--accent-hex)] group-hover:bg-[var(--accent-hex-hover)] px-3 py-2 rounded-md shrink-0">{t("leader.summary.cta")}</span>
            </button>

            {insights && (insights.mvp || insights.biggest_climber || insights.biggest_dropper) && (
                <InsightsBanner insights={insights} t={t} />
            )}

            <div className="rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] overflow-hidden">
                {/* header row */}
                <div className="hidden sm:flex items-center px-4 py-2 border-b border-[var(--border-muted)] text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)]">
                    <div className="w-10 text-center">#</div>
                    <div className="flex-1">{t("leader.col.player")}</div>
                    <div className="w-12 text-center">±</div>
                    <div className="w-16 text-right">{t("leader.group_pts")}</div>
                    <div className="w-16 text-right">{t("leader.knock_pts")}</div>
                    <div className="w-16 text-right">{t("leader.col.bonus")}</div>
                    <div className="w-20 text-right">{t("leader.col.total")}</div>
                </div>
                {rows.map((r) => {
                    const isMe = r.user_id === currentUserId;
                    const change = r.rank_change || 0;
                    return (
                        <div key={r.user_id} data-testid={`leader-row-${r.user_id}`}
                            className={`flex flex-wrap sm:flex-nowrap items-center px-4 py-3 border-b border-[var(--border-muted)] last:border-b-0 ${isMe ? "bg-[var(--accent-hex)]/10" : ""}`}>
                            <div className="w-10 text-center font-heading text-xl font-extrabold text-[var(--text-primary)]">{r.rank}</div>
                            <div className="flex-1 min-w-0">
                                <div className="font-heading text-base font-bold uppercase tracking-tight text-[var(--text-primary)] truncate">
                                    {r.user_name} {isMe && <span className="text-[10px] tracking-widest text-[var(--accent-hex)]">{t("leader.you")}</span>}
                                </div>
                            </div>
                            <div className="w-12 flex justify-center">
                                {change > 0 && <span className="inline-flex items-center gap-1 text-[#00FF66] text-xs font-bold"><ChevronUp className="h-4 w-4" />{change}</span>}
                                {change < 0 && <span className="inline-flex items-center gap-1 text-[#FF3B30] text-xs font-bold"><ChevronDown className="h-4 w-4" />{Math.abs(change)}</span>}
                                {change === 0 && <Minus className="h-4 w-4 text-[var(--text-muted)]" />}
                            </div>
                            <div className="w-16 text-right tabular-nums text-[var(--text-primary)]">
                                <span className="font-heading text-base font-bold" data-testid={`leader-group-${r.user_id}`}>{r.group_points_total ?? 0}</span>
                                <div className="sm:hidden text-[9px] uppercase tracking-widest font-bold text-[var(--text-muted)]">{t("leader.group_pts")}</div>
                            </div>
                            <div className="w-16 text-right tabular-nums text-[var(--text-primary)]">
                                <span className="font-heading text-base font-bold" data-testid={`leader-knock-${r.user_id}`}>{r.knockout_points_total ?? 0}</span>
                                <div className="sm:hidden text-[9px] uppercase tracking-widest font-bold text-[var(--text-muted)]">{t("leader.knock_pts")}</div>
                            </div>
                            <div className="w-16 text-right tabular-nums">
                                <span className="font-heading text-base font-bold text-amber-400" data-testid={`leader-bonus-${r.user_id}`}>{r.bonus_points_total ?? 0}</span>
                                <div className="sm:hidden text-[9px] uppercase tracking-widest font-bold text-[var(--text-muted)]">{t("leader.col.bonus")}</div>
                            </div>
                            <div className="w-20 text-right">
                                <span className="font-heading text-2xl font-extrabold text-[var(--accent-hex)] tabular-nums">{r.total_points}</span>
                                <div className="text-[9px] uppercase tracking-widest font-bold text-[var(--text-muted)]">{t("leader.pts")}</div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <HallOfFameModal open={hofOpen} onClose={() => setHofOpen(false)} rows={rows} pool={pool} />
        </div>
    );
}
